import { useAtom } from '@reatom/npm-react'
import { useRef, useState } from 'react'
import { styled } from 'styled-components'

import { useBorderBoxSize } from '@blade/ui-kit'

import { AmountInputNode } from './nodes/amount-input-node'
import { AssetNode } from './nodes/asset-node'
import { ConnectorNode } from './nodes/connector-node'
import { EdgeLine } from './nodes/edge-line'
import { WalletDestinationNodes } from './nodes/wallet-destination-nodes'
import { WalletSourceNode } from './nodes/wallet-source-node'

import type { DistributeFundsModel } from '../atoms'

type Props = {
  model: DistributeFundsModel
}

export const WalletDistributeGraph: React.FC<Props> = ({ model }) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const connectorRef = useRef<HTMLDivElement>(null)
  const amountInputNodeRef = useRef<HTMLDivElement>(null)

  const [assetNodeEl, setAssetNodeEl] = useState<HTMLDivElement | undefined>(
    undefined,
  )
  const [sourceWalletEl, setSourceWalletEl] = useState<
    HTMLDivElement | undefined
  >(undefined)

  const [sourceWallet] = useAtom(model.sourceWallet)
  const [assetCurrency] = useAtom(model.currency)

  const rootSize = useBorderBoxSize(rootRef)

  return (
    <Root ref={rootRef}>
      <svg
        width={rootSize.inlineSize}
        height={rootSize.blockSize}
        viewBox={`0 0 ${rootSize.inlineSize} ${rootSize.blockSize}`}
        style={{ pointerEvents: 'none', position: 'absolute', inset: 0 }}
        id="edges-container"
      >
        {sourceWalletEl ? (
          <EdgeLine
            rootEl={rootRef}
            sourceEl={sourceWalletEl}
            targetEl={connectorRef}
            sourcePosition="bottom-middle"
            targetPosition="center"
            direction="vertical-first"
            variant={sourceWallet ? 'default' : 'placeholder'}
          />
        ) : undefined}
        {assetNodeEl ? (
          <EdgeLine
            rootEl={rootRef}
            sourceEl={assetNodeEl}
            targetEl={connectorRef}
            sourcePosition="bottom-middle"
            targetPosition="center"
            direction="vertical-first"
            variant={assetCurrency ? 'default' : 'placeholder'}
          />
        ) : undefined}
        <EdgeLine
          rootEl={rootRef}
          sourceEl={connectorRef}
          targetEl={amountInputNodeRef}
          sourcePosition="center"
          targetPosition="top-middle"
          direction="vertical-first"
          variant={assetCurrency && sourceWallet ? 'default' : 'placeholder'}
        />
      </svg>

      <SourceNodes>
        <WalletSourceNode
          model={model}
          ref={(el) => setSourceWalletEl(el ?? undefined)}
        />
        <AssetNode
          model={model}
          ref={(el) => setAssetNodeEl(el ?? undefined)}
        />
      </SourceNodes>

      <SourceEdges>
        <ConnectorNode ref={connectorRef} />
      </SourceEdges>

      <AmountContainer>
        <AmountInputNode model={model} ref={amountInputNodeRef} />
      </AmountContainer>

      <WalletDestinationNodes
        model={model}
        rootEl={rootRef}
        amountInputNodeRef={amountInputNodeRef}
      />
    </Root>
  )
}

const Root = styled.div`
  position: relative;
`

const SourceNodes = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`

const SourceEdges = styled.div`
  height: 50px;
  position: relative;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: center;
`

const AmountContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: center;
`
